<template>
	<div>
		<b-modal 
			@hidden="hideModal" 
			ref="recordsPerDayModal" 
			id="recordsPerDayModal" 
			:title="modalTitle"
            :ok-only="true"
            :ok-variant="'secondary'"
            :ok-title="$t('misc.cancel')"
			size="huge"
		>
            <div class="col-lg-12 text-left p-0">
                <button class="btn btn-warning" @click="goBack">
                    <i class="fa fa-arrow-left" aria-hidden="true"></i> 
                    {{ $t('table.go_back') }}
                </button>
            </div>

			<data-table
				v-if="tableLoaded"
				:search="false"
				:exportable="false"
				:rows="getData.rowData"
				:columns="columnData"
				:perPage="perPage"
				:rowsCount="rowsCount"
				:currentPage="currentPage"
				:pageCount="pageCount"
                :grandTotal="true"
                :grandTotalData="grandTotalData"
				@rowClickDelete="onDeleteRow"
                @rowClickEdit="onOpenModal('edit', ...arguments)"
                @rowCloneRow="onOpenModal('clone', ...arguments)"
				:paginate="false"
			>
			</data-table>

			<div class="row mt-4">
				<div class="col-sm-12">
					<span v-if="this.$store.getters.hasPermission(['project_hours_recording'], ['create']) && getData.rowData.length > 0">
						<i class="fa fa-2x fa-clone text-default"></i> - {{$t('general.buttons.clone')}}
					</span>
				</div>
			</div>
		</b-modal>

        <hours-recording-modal
            ref="hoursRecordingModal"
            :modalTitle="actionTitle"
            :tableName="tableName"
            :tableNameTranslated="tableNameTranslated"
            :isEdit="isEdit"
            :model.sync="model"
            @onLoadTable="onLoadTables"
        >
        </hours-recording-modal>

		<confirm-dialog ref="deleteModal" :title="actionTitle" @buttonClick="deleteAction">
            {{ $t('messages.delete_record') }}
        </confirm-dialog>
	</div>
</template>

<script>
import Vue from 'vue';
import http from '@/libs/http';
import DataTable from '@/components/global/tables/datatables/DataTable.vue';
import ConfirmDialog from '@/components/global/modals/ConfirmDialog.vue';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import * as notificationIcons from '@/components/global/miscellaneous/notificationIcons.js';
import miniToastr from 'mini-toastr';
import Multiselect from 'vue-multiselect';
import HoursRecordingModal from '@/components/global/modals/HoursRecordingModal.vue';

export default {
	name: 'hours_recording_per_day_table',
	components: {
		DataTable,
		ConfirmDialog,
		flatPickr,
		notificationIcons,
		Multiselect,
		HoursRecordingModal,
	},
	props: {
		tableName: {
			type: String,
			required: true
		},
		copyDate: {
			type: String,
			default: ''
		},
		userID: {
			type: Number,
			default: ''
		},
		userFullName: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			tableNameTranslated: Vue.i18n.translate('general.administrative.hours_recording.title_modal'),
			tableLoaded: false,

			modalTitle: '',
			actionTitle: '',

			perPage: 0,
            rowsCount: 0,
            currentPage: 1,
            pageCount: 1,
			
            isEdit: false,
			row: null,
            grandTotalData: {},
			getData: {
                rowData: []
            },
            model: {
                id: null,
                discipline_id: null,
                team_id: null,
                drawing_id: null,
                user_id: null,
                change_order_id: null,
                tie_in_id: null,
                scaffolding_id: null,
                punchlist_id: null,
                project_setting_id: null,
                pressure_test_id: null,
                hours_no: null,
                date: null,
                start_time: null,
                end_time: null,
                others: null,
                holiday: null,
                sick: null,
                time_access: null,
            },
			columnData: [
                {
                    label: Vue.i18n.translate('misc.actions'),
                    field: '',
                    showEditButton: true,
                },
                {
                    label: Vue.i18n.translate('table.hours_no'),
                    field: 'hours_no',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.start_time'),
                    field: 'start_time',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.end_time'),
                    field: 'end_time',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.discipline'),
                    field: 'discipline.name',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.drawing'),
                    field: 'drawing.info',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.team'),
                    field: 'team.name',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.change_order'),
                    field: 'change_order.title',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.tie_in'),
                    field: 'tie_in.tie_in_nr',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.scaffolding'),
                    field: 'scaffolding.request_no',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.punchlist'),
                    field: 'punchlist.punch_no',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.work_type'),
                    field: 'work_type.value',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.pressure_test'),
                    field: 'pressure_test.test_circuit_no',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.holiday'),
                    field: 'holiday',
                    numeric: false,
                    html: false,
					showAsCheckRed: true,
                    cursor: false,
                },
                {
                    label: Vue.i18n.translate('table.sick'),
                    field: 'sick',
                    numeric: false,
                    html: false,
					showAsCheckRed: true,
                    cursor: false,
                },
                {
                    label: Vue.i18n.translate('table.time_access'),
                    field: 'time_access',
                    numeric: false,
                    html: false,
					showAsCheckRed: true,
                    cursor: false,
                },
			],
		}
	},
	methods: {
		successMsg(msg) {
            miniToastr.success(msg, Vue.i18n.translate('messages.success'), 5000);
        },
        errorMsg(msg) {
            miniToastr.error(msg, Vue.i18n.translate('messages.error'), 5000);
        },
		showModal () {
			this.modalTitle = Vue.i18n.translate('modal.general.records_for_date') + this.copyDate + ', ' + Vue.i18n.translate('modal.general.for_the_user') + '' + this.userFullName;
			this.onLoadRecordsTable();
		},
		hideModal () {
			this.tableLoaded = false;
		},
        onLoadTables () {
            this.$emit('onLoadTable');
            this.onLoadRecordsTable();
        },
		onLoadRecordsTable () {
            http.route(this.tableName + '/records_per_day?project_id=' + this.$route.params.id + '&filters[user_id]=' + this.userID + '&filters[date]=' + this.copyDate).get()
			.then(response => {
                if (!response.isError) {
                    this.perPage     = response.pagination.limit;
                    this.rowsCount   = response.pagination.totalEntries;
                    this.currentPage = response.pagination.currentPage;
                    this.pageCount   = response.pagination.totalPages;
                    var data         = response.data;
                    if (typeof response.other.grand_total != 'undefined') {
                        this.grandTotalData = response.other.grand_total;
                    }
					data.forEach(field => {
						if (field.discipline == null) {
							field.discipline = {
								name: ''
							}
						}

						if (field.drawing == null) {
							field.drawing = {
								info: ''
							}
						}

						if (field.team == null) {
							field.team = {
								name: ''
							}
						}

						if (field.change_order == null) {
							field.change_order = {
								title: ''
							}
						}

						if (field.tie_in == null) {
							field.tie_in = {
								tie_in_nr: ''
							}
						}

						if (field.scaffolding == null) {
							field.scaffolding = {
								request_no: ''
							}
						}

						if (field.punchlist == null) {
							field.punchlist = {
								punch_no: ''
							}
						}

						if (field.work_type == null) {
							field.work_type = {
								value: ''
							}
						}

						if (field.pressure_test == null) {
							field.pressure_test = {
								test_circuit_no: ''
							}
						}
					});
                    this.getData.rowData = data;
                    this.tableLoaded = true;
					this.$refs.recordsPerDayModal.show();
                } else {
                    Object.keys(response.errorMessage).forEach(key => {
                        this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                    });
                }
            });
        },
		onOpenModal (type, row) {
            this.$refs.hoursRecordingModal.clearAll();
            this.isEdit = (type == 'edit');
            this.actionTitle = Vue.i18n.translate(this.isEdit ? 'modal.general.edit' : 'modal.general.clone') + ' ' + this.tableNameTranslated;
            this.model.id                 = row.id;
            this.model.discipline_id      = row.discipline_id;
            this.model.team_id            = row.team_id;
            this.model.drawing_id         = row.drawing_id;
            this.model.user_id            = row.user_id;
            this.model.change_order_id    = row.change_order_id;
            this.model.tie_in_id          = row.tie_in_id;
            this.model.scaffolding_id     = row.scaffolding_id;
            this.model.punchlist_id       = row.punchlist_id;
            this.model.project_setting_id = row.project_setting_id;
            this.model.pressure_test_id   = row.pressure_test_id;
            this.model.hours_no           = row.hours_no;
            this.model.date               = row.date;
            this.model.start_time         = row.start_time;
            this.model.end_time           = row.end_time;
            this.model.others             = row.others;
            this.model.holiday            = Boolean(row.holiday);
            this.model.sick               = Boolean(row.sick);
            this.model.time_access        = Boolean(row.time_access);

            //Discipline
            if (
                typeof row.discipline.name != 'undefined' &&
                row.discipline.name != ''
            ) {
                this.$refs.hoursRecordingModal.selectedDiscipline = row.discipline;
            }

            //Drawing
            if (
                typeof row.drawing.info != 'undefined' &&
                row.drawing.info != ''
            ) {
                this.$refs.hoursRecordingModal.selectedDrawing = row.drawing;
            }

            //Team
            if (
                typeof row.team.name != 'undefined' &&
                row.team.name != ''
            ) {
                this.$refs.hoursRecordingModal.selectedTeam = row.team;
            }

            //ChangeOrder
            if (
                typeof row.change_order.title != 'undefined' &&
                row.change_order.title != ''
            ) {
                this.$refs.hoursRecordingModal.selectedChangeOrder = row.change_order;
            }

            //TieIn
            if (
                typeof row.tie_in.tie_in_nr != 'undefined' &&
                row.tie_in.tie_in_nr != ''
            ) {
                this.$refs.hoursRecordingModal.selectedTieIn = row.tie_in;
            }

            //Scaffolding
            if (
                typeof row.scaffolding.request_no != 'undefined' &&
                row.scaffolding.request_no != ''
            ) {
                this.$refs.hoursRecordingModal.selectedScaffolding = row.scaffolding;
            }

            //Punchlist
            if (
                typeof row.punchlist.punch_no != 'undefined' &&
                row.punchlist.punch_no != ''
            ) {
                this.$refs.hoursRecordingModal.selectedPunchlist = row.punchlist;
            }

            //WorkType
            if (
                typeof row.work_type.value != 'undefined' &&
                row.work_type.value != ''
            ) {
                this.$refs.hoursRecordingModal.selectedWorkType = row.work_type;
            }

            //PressureTest
            if (
                typeof row.pressure_test.test_circuit_no != 'undefined' &&
                row.pressure_test.test_circuit_no != ''
            ) {
                this.$refs.hoursRecordingModal.selectedPressureTest = row.pressure_test;
            }

            var array = [this.model.holiday, this.model.sick, this.model.time_access];
            array.forEach((value, key) => {
                if (Object.keys(value).length != 1) {
                    this.$refs.hoursRecordingModal.hideOthers(key);
                }
            });

            this.$refs.hoursRecordingModal.show();
		},
		onDeleteRow(row) {
            this.actionTitle = Vue.i18n.translate('modal.general.delete') + ' ' + this.tableNameTranslated;
            this.row = row;
            this.$refs.deleteModal.show();
        },
		deleteAction (button) { 
            if (button == 1) {
                let data = http.route(this.tableName + '/' + this.row.id).delete();
                data.then(response => {
                    if (response.isError == false) {
                        this.onLoadTables();
                        this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.deleted'));
                    } else {
                        this.errorMsg(Vue.i18n.translate('errors.error_delete'));
                    }
                });
            }

            this.$refs.deleteModal.hide();
        },
        goBack () {
            this.$refs.recordsPerDayModal.hide();
            this.$emit('goBack');
        }
	}
}
</script>



// WEBPACK FOOTER //
// src/components/pages/project/administrative/hours_recording/hours_recording_per_day_table.vue